import type { ChangeEvent, FocusEvent } from 'react';
import { useRef } from 'react';
import { cn } from '../../../common/functions/cn';
import { combineRefs } from '../../../common/functions/combine-refs';
import RequiredIndicator from '../../common/required-indicator/required-indicator.component';
import type { DynamicFieldComponentProps } from '../../dynamic-form/dynamic-form.interfaces';
import EmptyFieldLabel from '../../labels/empty-field-label';
import ErrorLabel from '../../labels/error-label';
import FieldLabel from '../../labels/field-label';
import OdinIcon from '../../odin-icon/odin-icon.component';
import { InputGroup, InputGroupAddon, InputGroupButton, InputGroupInput } from '../input-group/input-group';
import { getCurrentTime } from './time-utils';

export interface TimeProps extends DynamicFieldComponentProps<string | null> {
  /**
   * When `true`, automatically populates the field with the current local time on focus if the field is empty.
   *
   * @default true
   */
  populateOnFocus?: boolean;
}

export const Time = ({
  id,
  name,
  label,
  value,
  error,
  placeholder,
  className,
  inputRef,
  readOnly,
  required,
  disabled,
  onChange,
  onFocus,
  labelStyles,
  populateOnFocus = true,
}: TimeProps) => {
  const internalRef = useRef<HTMLInputElement>(null);
  const combinedRef = combineRefs(inputRef, internalRef);

  const identifier = id ? String(id) : name;

  const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;
    if (value) {
      onChange?.(value);
    } else {
      handleClear();
    }
  };

  const handleFocus = (event: FocusEvent<HTMLInputElement>) => {
    if (populateOnFocus && !readOnly && !event.target.value) {
      onChange?.(getCurrentTime());
    }
    onFocus?.();
  };

  const handleClear = () => {
    onChange?.(null);
  };

  const showClear = !!value && !readOnly && !disabled;
  const isEmpty = !value || value.length === 0;

  const inputStyles = cn(
    'appearance-none',
    '[&::-webkit-calendar-picker-indicator]:hidden [&::-webkit-calendar-picker-indicator]:appearance-none',
    readOnly && 'cursor-default pl-0',
    className
  );

  return (
    <div className="flex w-full flex-col">
      {label && (
        <div className="flex">
          <FieldLabel label={label} name={identifier} labelStyles={labelStyles} />
          <RequiredIndicator readOnly={readOnly} required={required} />
        </div>
      )}
      <InputGroup className={cn(readOnly && 'border-none', readOnly && isEmpty && 'hidden')}>
        <InputGroupInput
          ref={combinedRef}
          type="time"
          id={identifier}
          name={name}
          value={value ?? ''}
          placeholder={placeholder}
          onChange={handleChange}
          onFocus={handleFocus}
          readOnly={readOnly}
          disabled={disabled}
          aria-invalid={!!error}
          className={inputStyles}
        />
        {!readOnly && (
          <InputGroupAddon>
            <OdinIcon icon="Clock" className="size-4 text-foreground" />
          </InputGroupAddon>
        )}
        {showClear && (
          <InputGroupAddon align="inline-end">
            <InputGroupButton
              variant="ghost"
              size="icon-xs"
              tabIndex={-1}
              aria-label="Clear time"
              onClick={handleClear}
            >
              <OdinIcon icon="X" />
            </InputGroupButton>
          </InputGroupAddon>
        )}
      </InputGroup>
      {readOnly && isEmpty && <EmptyFieldLabel />}
      {error && <ErrorLabel role="alert">{error}</ErrorLabel>}
    </div>
  );
};
